import { useNavigate } from "react-router-dom";

// Candidate-facing "what to expect" page — explains the journey from
// application through to placement (and back to the pool) in the
// candidate's own terms, mirroring the status labels they actually see
// on their dashboard (see STATUS_DISPLAY in ApplicantPortal.jsx).
//
// Two videos, both optional (each hides itself via onError if missing):
//   public/videos/candidate-sign-in-guide.mp4 — registering/signing in
//   public/videos/how-it-works.mp4            — the application/contract journey

const BRAND = {
  primary: "#9c27b0",
  primaryDark: "#6a0080",
  text: "#1a1a1a",
  textLight: "#666",
  border: "#e0d4e8",
  bg: "#faf5fc",
};

const STEPS = [
  {
    icon: "🔍",
    title: "Application Under Review",
    text: "You've applied — our team reviews your details and experience and matches you to positions in your province. This can take a little time while we find the right fit.",
  },
  {
    icon: "⭐",
    title: "You Have Been Short Listed!",
    text: "You're in the running for a placement. We may contact you for more information or to arrange a conversation — keep an eye on your phone and email.",
  },
  {
    icon: "📩",
    title: "Offer Letter Sent",
    text: "We'll email you an Offer Letter with the role, location, start date and salary. Open the link and review it carefully before signing online.",
  },
  {
    icon: "🎉",
    title: "Offer Letter Accepted",
    text: "Once you've signed, we prepare your Fixed-Term Employment Contract and any other onboarding documents (background check, confidentiality pledge, etc.) — each will arrive the same way, by email, for you to review and sign online.",
  },
  {
    icon: "✅",
    title: "You Are Currently Placed",
    text: "You're working! Your project, mobile unit and contract dates will show on your dashboard here. We track your contract's end date and follow up well before it's due.",
  },
  {
    icon: "📝",
    title: "If Your Contract Is Extended",
    text: "Projects sometimes run longer than planned. If yours is extended, you'll get a Contract Addendum by email to review and sign — the same simple online process as your original contract.",
  },
  {
    icon: "↩️",
    title: "Contract Ended",
    text: "When a placement ends, you return to the pool so we can match you to your next opportunity — your profile and documents stay on file, so there's nothing to redo.",
  },
];

export default function HowItWorks() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: "100vh", background: BRAND.bg, fontFamily: "system-ui, sans-serif" }}>
      <div style={{ background: BRAND.primary, padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 18, letterSpacing: "-0.3px" }}>guud vision</div>
        <button
          onClick={() => navigate("/portal")}
          style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 13 }}
        >
          ← Back to My Dashboard
        </button>
      </div>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "28px 16px 48px" }}>
        <h1 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 800, color: BRAND.text }}>How to Register &amp; Sign In</h1>
        <p style={{ color: BRAND.textLight, marginTop: 0, fontSize: 14 }}>
          A quick walkthrough of creating your account and signing in to the Talent Pool portal.
        </p>
        <video
          src="/videos/candidate-sign-in-guide.mp4"
          controls
          playsInline
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          style={{ width: "100%", borderRadius: 12, background: "#000", margin: "16px 0 32px" }}
        >
          Your browser cannot play this video.
        </video>

        <h1 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 800, color: BRAND.text }}>What to Expect</h1>
        <p style={{ color: BRAND.textLight, marginTop: 0, fontSize: 14 }}>
          Here's the journey from application to placement, and what each stage on your dashboard means for you.
        </p>

        <video
          src="/videos/how-it-works.mp4"
          controls
          playsInline
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          style={{ width: "100%", borderRadius: 12, background: "#000", margin: "16px 0 24px" }}
        >
          Your browser cannot play this video.
        </video>

        <div style={{ display: "grid", gap: 14 }}>
          {STEPS.map((s) => (
            <div key={s.title} style={{ background: "#fff", border: `1px solid ${BRAND.border}`, borderRadius: 14, padding: "16px 18px", display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{ fontSize: 26, lineHeight: 1, flexShrink: 0 }}>{s.icon}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14.5, color: BRAND.primaryDark, marginBottom: 3 }}>{s.title}</div>
                <div style={{ fontSize: 13.5, color: BRAND.text, lineHeight: 1.5 }}>{s.text}</div>
              </div>
            </div>
          ))}
        </div>

        <p style={{ color: BRAND.textLight, fontSize: 12.5, marginTop: 24, textAlign: "center" }}>
          Questions about where you are in the process? Reach out to the Guud Vision Talent Pool team.
        </p>
      </div>
    </div>
  );
}
